import React, { useState, useRef } from 'react';
import { Send, Paperclip, X, File as FileIcon, Loader2 } from 'lucide-react';
import { Attachment } from '../../types';
import { chatService } from '../../services/chatService';

interface ChatInputProps {
  onSendMessage: (content: string, attachments: Attachment[]) => Promise<void>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage }) => {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!text.trim() && attachments.length === 0) || isUploading) return;
    await onSendMessage(text, attachments);
    setText('');
    setAttachments([]);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploading(true);
      try {
        const file = e.target.files[0];
        if (file.size > 5 * 1024 * 1024) { alert("File too large (Max 5MB)"); return; }
        const attachment = await chatService.uploadFile(file);
        setAttachments(prev => [...prev, attachment]);
      } catch (error: any) {
        console.error("Upload failed", error);
        const msg = error?.code || error?.message || "Unknown error";
        alert(`Failed to attach file: ${msg}`);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (id: string) => setAttachments(prev => prev.filter(a => a.id !== id));

  return (
    <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      {attachments.length > 0 && (
        <div className="flex gap-3 mb-3 overflow-x-auto pb-2">
          {attachments.map(att => (
            <div key={att.id} className="relative group flex-shrink-0">
              <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                {att.type === 'image' ? <img src={att.url} alt="preview" className="w-full h-full object-cover" /> : <FileIcon className="w-8 h-8 text-gray-400" />}
              </div>
              <button onClick={() => removeAttachment(att.id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="p-3 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
          {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
        </button>
        <div className="flex-1 bg-gray-100 rounded-2xl px-4 py-2 focus-within:ring-2 focus-within:ring-blue-100 transition-shadow">
          <input type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." className="w-full bg-transparent border-none focus:ring-0 outline-none text-gray-800 placeholder-gray-400 py-1" />
        </div>
        <button type="submit" disabled={(!text.trim() && attachments.length === 0) || isUploading} className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"><Send className="w-5 h-5" /></button>
      </form>
    </div>
  );
};
